import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';

export interface ScormDataChangeEvent {
  parameter: string;
  oldValue: any;
  newValue: any;
}

export interface ScormTrackingData {
  [key: string]: string;
}

@Injectable({
  providedIn: 'root'
})
export class ScormApiService {
  private apiCalls: string[] = [];
  private scormData: ScormTrackingData = {
    'cmi.core.lesson_status': '',
    'cmi.core.score.raw': '',
    'cmi.core.lesson_location': '',
    'cmi.suspend_data': '',
    'cmi.core.session_time': '',
    'cmi.core.exit': '',
    'cmi.completion_status': '',
    'cmi.progress_measure': ''
  };
  
  private dataChangeSubject = new Subject<ScormDataChangeEvent>();
  private completionSubject = new Subject<string>();
  
  public dataChange$ = this.dataChangeSubject.asObservable();
  public completion$ = this.completionSubject.asObservable();
  
  constructor() { }
  
  /**
   * Get all recorded API calls
   */
  getApiCalls(): string[] {
    return this.apiCalls;
  }
  
  /**
   * Clear the API calls history
   */
  clearApiCalls(): void {
    this.apiCalls = [];
  }
  
  /**
   * Get all stored SCORM data
   */
  getScormData(): ScormTrackingData {
    return { ...this.scormData };
  }
  
  /**
   * Set up the SCORM API on the window object
   */
  setupScormAPI(): void {
    console.log('Setting up SCORM API on window object');
    
    const service = this; // Store reference to service for use in API methods
    
    // SCORM 1.2 API
    (window as any).API = {
      LMSInitialize: function(param: string) {
        console.log('LMSInitialize called with:', param);
        service.apiCalls.push(`LMSInitialize("${param}")`);
        return 'true';
      },
      
      LMSFinish: function(param: string) {
        console.log('LMSFinish called with:', param);
        service.apiCalls.push(`LMSFinish("${param}")`);

        // If the course status has already been set to completed / passed but the component
        // hasn't yet picked it up (some packages only set the status right before finish),
        // emit the completion event explicitly here.
        const status = service.scormData['cmi.core.lesson_status'];
        if (status === 'completed' || status === 'passed') {
          service.completionSubject.next(status);
        }

        return 'true';
      },
      
      LMSGetValue: function(param: string) {
        console.log('LMSGetValue called with:', param);
        service.apiCalls.push(`LMSGetValue("${param}")`);
        
        // Return stored data if available
        if (service.scormData[param] !== undefined) {
          return service.scormData[param];
        }
        
        return '';
      },
      
      LMSSetValue: function(param: string, value: string) {
        console.log('LMSSetValue called with:', param, value);
        service.apiCalls.push(`LMSSetValue("${param}", "${value}")`);
        
        // Store the data
        if (param) {
          const oldValue = service.scormData[param];
          service.scormData[param] = value;
          
          // Emit data change event
          service.dataChangeSubject.next({
            parameter: param,
            oldValue: oldValue,
            newValue: value
          });
          
          // Completion detection via status or >=70 % progress
          if ((param === 'cmi.core.lesson_status' && (value === 'completed' || value === 'passed')) ||
              (param === 'cmi.progress_measure' && parseFloat(value) >= 0.7)) {
            console.log('COURSE COMPLETION DETECTED!');
            service.apiCalls.push(`*** COURSE COMPLETION DETECTED! ***`);
            service.completionSubject.next(value);
          }
        }
        
        return 'true';
      },
      
      LMSCommit: function(param: string) {
        console.log('LMSCommit called with:', param);
        service.apiCalls.push(`LMSCommit("${param}")`);
        return 'true';
      },
      
      LMSGetLastError: function() {
        console.log('LMSGetLastError called');
        service.apiCalls.push(`LMSGetLastError()`);
        return '0';
      },
      
      LMSGetErrorString: function(errorCode: string) {
        console.log('LMSGetErrorString called with:', errorCode);
        service.apiCalls.push(`LMSGetErrorString("${errorCode}")`);
        return 'No error';
      },
      
      LMSGetDiagnostic: function(errorCode: string) {
        console.log('LMSGetDiagnostic called with:', errorCode);
        service.apiCalls.push(`LMSGetDiagnostic("${errorCode}")`);
        return 'No diagnostic information';
      }
    };
    
    // SCORM 2004 API (compatible mapping to 1.2)
    (window as any).API_1484_11 = {
      Initialize: function(param: string) {
        console.log('Initialize called (2004)');
        service.apiCalls.push(`Initialize("${param}") [2004]`);
        return 'true';
      },
      
      Terminate: function(param: string) {
        console.log('Terminate called (2004)');
        service.apiCalls.push(`Terminate("${param}") [2004]`);

        const status = service.scormData['cmi.core.lesson_status'];
        if (status === 'completed' || status === 'passed') {
          service.completionSubject.next(status);
        }

        return 'true';
      },
      
      GetValue: function(param: string) {
        console.log('GetValue called (2004):', param);
        service.apiCalls.push(`GetValue("${param}") [2004]`);
        
        // Map 2004 to 1.2 parameter names
        let scorm12Param = param;
        if (param === 'cmi.completion_status') scorm12Param = 'cmi.core.lesson_status';
        else if (param === 'cmi.score.raw') scorm12Param = 'cmi.core.score.raw';
        else if (param === 'cmi.location') scorm12Param = 'cmi.core.lesson_location';
        else if (param === 'cmi.success_status') scorm12Param = 'cmi.core.lesson_status';
        
        // Return stored data if available
        if (service.scormData[scorm12Param] !== undefined) {
          return service.scormData[scorm12Param];
        }
        
        return '';
      },
      
      SetValue: function(param: string, value: string) {
        console.log('SetValue called (2004):', param, value);
        service.apiCalls.push(`SetValue("${param}", "${value}") [2004]`);
        
        // Map 2004 to 1.2 parameter names and values
        let scorm12Param = param;
        let scorm12Value = value;
        
        if (param === 'cmi.completion_status') {
          scorm12Param = 'cmi.core.lesson_status';
        } 
        else if (param === 'cmi.score.raw') {
          scorm12Param = 'cmi.core.score.raw';
        }
        else if (param === 'cmi.location') {
          scorm12Param = 'cmi.core.lesson_location';
        }
        else if (param === 'cmi.success_status') {
          scorm12Param = 'cmi.core.lesson_status';
        }
        
        // Store the data
        if (scorm12Param) {
          const oldValue = service.scormData[scorm12Param];
          service.scormData[scorm12Param] = scorm12Value;
          
          // Emit data change event
          service.dataChangeSubject.next({
            parameter: scorm12Param,
            oldValue: oldValue,
            newValue: scorm12Value
          });
          
          // Completion detection via status or >=70 % progress
          const progressComplete = (scorm12Param === 'cmi.progress_measure' && parseFloat(scorm12Value) >= 0.7);

          if ((param === 'cmi.completion_status' || param === 'cmi.success_status' || scorm12Param === 'cmi.core.lesson_status') &&
              (value === 'completed' || value === 'passed') || progressComplete) {
            console.log('COURSE COMPLETION DETECTED! (2004)');
            service.apiCalls.push(`*** COURSE COMPLETION DETECTED! (2004) ***`);
            service.completionSubject.next(value);
          }
        }
        
        return 'true';
      },
      
      Commit: function(param: string) {
        console.log('Commit called (2004)');
        service.apiCalls.push(`Commit("${param}") [2004]`);
        return 'true';
      },
      
      GetLastError: function() {
        console.log('GetLastError called (2004)');
        service.apiCalls.push(`GetLastError() [2004]`);
        return '0';
      },
      
      GetErrorString: function(errorCode: string) {
        console.log('GetErrorString called (2004):', errorCode);
        service.apiCalls.push(`GetErrorString("${errorCode}") [2004]`);
        return 'No error';
      },
      
      GetDiagnostic: function(errorCode: string) {
        console.log('GetDiagnostic called (2004):', errorCode);
        service.apiCalls.push(`GetDiagnostic("${errorCode}") [2004]`);
        return 'No diagnostic information';
      }
    };
    
    console.log('SCORM APIs created on window:', 
                'API:', (window as any).API, 
                'API_1484_11:', (window as any).API_1484_11);
  }

  /**
   * Inject the SCORM API into an iframe
   */
  injectApiIntoIframe(iframe: HTMLIFrameElement): void {
    console.log('Injecting SCORM API into iframe');
    
    try {
      if (iframe.contentWindow) {
        console.log('Setting API directly on iframe.contentWindow');
        (iframe.contentWindow as any).API = (window as any).API;
        (iframe.contentWindow as any).API_1484_11 = (window as any).API_1484_11;
        
        // Also try to set it on any child frames that might exist
        try {
          const childFrames = iframe.contentDocument?.querySelectorAll('iframe');
          if (childFrames && childFrames.length > 0) {
            console.log(`Found ${childFrames.length} child frames, injecting API...`);
            childFrames.forEach((childFrame: HTMLIFrameElement, index: number) => {
              try {
                console.log(`Injecting API into child frame ${index}...`);
                if (childFrame.contentWindow) {
                  (childFrame.contentWindow as any).API = (window as any).API;
                  (childFrame.contentWindow as any).API_1484_11 = (window as any).API_1484_11;
                }
              } catch (e) {
                console.log(`Could not access child frame ${index}:`, e);
              }
            });
          }
        } catch (e) {
          console.log('Could not access child frames:', e);
        }
      }
    } catch (e) {
      console.error('Error injecting API into iframe:', e);
    }
  }
  
  /**
   * Set up a periodic check to ensure the API remains accessible
   */
  setupApiCheckInterval(iframe: HTMLIFrameElement): number {
    console.log('Setting up periodic API check');
    
    return window.setInterval(() => {
      console.log('Periodic API check...');
      try {
        if (iframe.contentWindow) {
          console.log('Re-setting API on iframe.contentWindow');
          (iframe.contentWindow as any).API = (window as any).API;
          (iframe.contentWindow as any).API_1484_11 = (window as any).API_1484_11;
          
          // Check if there are any new child frames
          try {
            const childFrames = iframe.contentDocument?.querySelectorAll('iframe');
            if (childFrames && childFrames.length > 0) {
              childFrames.forEach((childFrame: HTMLIFrameElement, index: number) => {
                try {
                  if (childFrame.contentWindow) {
                    (childFrame.contentWindow as any).API = (window as any).API;
                    (childFrame.contentWindow as any).API_1484_11 = (window as any).API_1484_11;
                  }
                } catch (e) {
                  // Ignore cross-origin access errors
                }
              });
            }
          } catch (e) {
            // Ignore cross-origin access errors
          }
        }
      } catch (e) {
        console.error('Error in API check interval:', e);
      }
    }, 2000); // Check every 2 seconds
  }
  
  /**
   * Manually set the completion status
   */
  forceCompletion(): void {
    const oldValue = this.scormData['cmi.core.lesson_status'];
    this.scormData['cmi.core.lesson_status'] = 'completed';
    
    this.dataChangeSubject.next({
      parameter: 'cmi.core.lesson_status',
      oldValue: oldValue,
      newValue: 'completed'
    });
    
    this.completionSubject.next('completed');
    this.apiCalls.push('*** FORCED COMPLETION ***');
  }
} 